/**
 * FastestPathToThousand — pinned to the top of the paid dashboard.
 *
 * Curated grid of the 10 tracks that reliably ship a first £1K online
 * fastest, ranked to match the "10 Ways People Make Money With AI"
 * infographic. Shown to paid members only. Free users see a locked teaser.
 *
 * Includes a Duolingo-style progress tracker (started / completed counts)
 * driven by ProgressContext.countFor(slug).
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, Lock, Check, Flame } from "lucide-react";
import { CategoryIcon } from "./CategoryIcon";
import { useProgress } from "../contexts/ProgressContext";

// Ranked 1-10 exactly as per the infographic. Each entry pins to a real vault
// track slug — no phantom cards. Update this list if we add / rename tracks.
export const FASTEST_PATH_SLUGS = [
  { slug: "ai-content-writing",       label: "AI Content Writing",       tag: "Blog + copy for clients" },
  { slug: "ecommerce-shopify",        label: "Online Store Setup",       tag: "Shopify products that sell" },
  { slug: "youtube-automation",       label: "YouTube Automation",       tag: "Faceless AdSense channels" },
  { slug: "ebook-writing",            label: "E-Book Creation",          tag: "Ship & sell on KDP" },
  { slug: "smma-agency",              label: "Social Media Management",  tag: "SMMA for local clients" },
  { slug: "ai-consulting",            label: "AI Consulting",            tag: "Strategy for SMBs" },
  { slug: "print-on-demand",          label: "Print-on-Demand",          tag: "Designs on t-shirts + mugs" },
  { slug: "email-marketing",          label: "Email Marketing",          tag: "High-converting broadcasts" },
  { slug: "no-code-app-builder",      label: "No-Code App Ideas",        tag: "Bubble / Softr MVPs" },
  { slug: "digital-products",         label: "Digital Product Creation", tag: "Create once, sell forever" },
];

export function FastestPathToThousand({ categories, isPaid }) {
  const { countFor } = useProgress();
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const bySlug = Object.fromEntries(categories.map((c) => [c.slug, c]));
  const rows = FASTEST_PATH_SLUGS.map((row, i) => {
    const cat = bySlug[row.slug];
    return { ...row, rank: i + 1, cat };
  }).filter((r) => r.cat);

  if (rows.length === 0) return null;

  // Duolingo-style stats — only counted for paid users (progress endpoints
  // require auth + a saved item, so free users always show 0/0).
  const started = isPaid ? rows.filter((r) => countFor(r.slug) > 0).length : 0;
  const completed = isPaid
    ? rows.filter((r) => countFor(r.slug) >= (r.cat.prompt_count || 999)).length
    : 0;
  const pct = Math.round((completed / rows.length) * 100);

  return (
    <section
      className="relative border-b border-amber-500/20 bg-gradient-to-b from-amber-500/[0.03] via-black to-black"
      data-testid="fastest-path-to-1k"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-amber-300 text-[10px] md:text-xs tracking-[0.35em] uppercase mb-3 font-mono">
              <Zap size={12} className="animate-pulse" strokeWidth={2.5} />
              Fastest path to a first sale
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] text-white">
              The top 10 for the{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(135deg, #ddc4ff 0%, #b070ff 45%, #a855f7 100%)" }}
              >
                quickest first sale
              </span>
              .
            </h2>
            <p className="text-zinc-400 text-sm md:text-base mt-3 max-w-2xl leading-relaxed">
              Ten tracks. Ranked by how quickly a beginner can ship something a customer
              can actually buy. Start at #01 and don&apos;t skip steps. Results vary — nothing here is guaranteed income.
            </p>
          </div>
          {!isPaid && (
            <span
              className="inline-flex items-center gap-2 self-start bg-purple-500/15 border border-purple-500/60 text-purple-200 text-[10px] md:text-xs tracking-[0.25em] uppercase px-3 py-1.5 leading-none font-bold"
              data-testid="fastest-path-locked-pill"
            >
              <Lock size={12} strokeWidth={2.5} /> Members only
            </span>
          )}
        </div>

        {/* Progress tracker — paid only */}
        {isPaid && (
          <div
            className="mb-8 md:mb-10 border border-amber-500/25 bg-gradient-to-r from-amber-500/[0.05] via-transparent to-amber-500/[0.05] px-4 md:px-6 py-4 md:py-5"
            data-testid="fastest-path-progress"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Flame size={18} className="text-amber-300 shrink-0 drop-shadow-[0_0_10px_rgba(176,112,255,0.5)]" strokeWidth={2.25} />
                <div className="text-sm md:text-base">
                  <span className="text-white font-semibold" data-testid="fastest-path-started-count">
                    {started} of {rows.length} started
                  </span>
                  <span className="text-zinc-500 mx-2">·</span>
                  <span className="text-amber-300 font-semibold" data-testid="fastest-path-completed-count">
                    {completed} of {rows.length} completed
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:min-w-[240px]">
                <div className="flex-1 h-2 bg-zinc-800/70 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-700 rounded-full"
                    style={{ width: `${pct}%` }}
                    data-testid="fastest-path-progress-bar"
                  />
                </div>
                <span className="text-amber-300 text-xs font-mono tracking-wider shrink-0 w-10 text-right">
                  {pct}%
                </span>
              </div>
            </div>
            {started === 0 && (
              <p className="mt-3 text-xs md:text-sm text-zinc-500">
                Tap tile #01 to start. Every prompt you tick inside a track counts towards this bar.
              </p>
            )}
            {completed > 0 && completed < rows.length && (
              <p className="mt-3 text-xs md:text-sm text-amber-200/80">
                Nice — {completed} banked. Keep the streak alive and clear the top 10.
              </p>
            )}
            {completed === rows.length && (
              <p className="mt-3 text-xs md:text-sm text-amber-200 font-semibold">
                All 10 completed. You&apos;ve run the fastest-path playbook end-to-end.
              </p>
            )}
          </div>
        )}

        {/* Grid — 10 tiles, ranked 01-10 */}
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4"
          data-testid="fastest-path-grid"
        >
          {rows.map(({ rank, label, tag, slug, cat }) => {
            const locked = !isPaid;
            const to = locked ? "/pricing" : `/vault/${slug}`;
            const done = isPaid ? countFor(slug) : 0;
            const total = cat.prompt_count || 0;
            const trackDone = total > 0 && done >= total;
            const trackStarted = done > 0 && !trackDone;
            return (
              <Link
                key={slug}
                to={to}
                className={`group relative flex flex-col p-4 md:p-5 border transition-all duration-300 ${
                  locked
                    ? "border-zinc-800 bg-[#0a0a0a] hover:border-purple-500/60 hover:bg-[#0d0a14]"
                    : trackDone
                      ? "border-amber-400/70 bg-gradient-to-b from-amber-500/[0.10] to-transparent hover:border-amber-300 shadow-[0_0_18px_-8px_rgba(176,112,255,0.55)]"
                      : "border-amber-500/20 bg-gradient-to-b from-amber-500/[0.04] to-transparent hover:border-amber-400/60 hover:from-amber-500/[0.08]"
                }`}
                data-testid={`fastest-path-tile-${slug}`}
              >
                {/* Rank badge */}
                <div className="flex items-start justify-between mb-3">
                  <span
                    className={`font-mono text-2xl md:text-3xl font-black tracking-tighter leading-none transition-colors ${
                      locked
                        ? "text-zinc-700 group-hover:text-purple-400"
                        : trackDone
                          ? "text-amber-300"
                          : "text-amber-400/80 group-hover:text-amber-300"
                    }`}
                  >
                    {String(rank).padStart(2, "0")}
                  </span>
                  <CategoryIcon image={cat.image_icon} lucide={cat.icon} size={40} />
                </div>

                <h3 className="font-display text-base md:text-lg font-bold tracking-tight text-white leading-tight mb-1.5">
                  {label}
                </h3>
                <p className="text-zinc-500 text-xs leading-snug flex-1 mb-3">
                  {tag}
                </p>

                {/* Progress pill — paid only, dynamic per-track */}
                {isPaid && (
                  <div className="flex items-center justify-between mb-2">
                    {trackDone ? (
                      <span
                        className="inline-flex items-center gap-1 text-amber-300 text-[10px] font-bold tracking-[0.15em] uppercase"
                        data-testid={`fastest-path-tile-status-${slug}`}
                      >
                        <Check size={11} strokeWidth={3} /> Done
                      </span>
                    ) : trackStarted ? (
                      <span
                        className="inline-flex items-center gap-1 text-emerald-300 text-[10px] font-mono tracking-wide"
                        data-testid={`fastest-path-tile-status-${slug}`}
                      >
                        {done}/{total}
                      </span>
                    ) : (
                      <span
                        className="text-zinc-600 text-[10px] font-mono tracking-wide"
                        data-testid={`fastest-path-tile-status-${slug}`}
                      >
                        {total} prompts
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1.5 text-[10px] md:text-xs font-medium tracking-wide transition-all duration-300 group-hover:gap-2 ${
                        trackDone ? "text-amber-300 group-hover:text-amber-200" : "text-amber-300 group-hover:text-amber-200"
                      }`}
                    >
                      Open <ArrowUpRight size={14} />
                    </span>
                  </div>
                )}
                {!isPaid && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] md:text-xs font-medium tracking-wide text-purple-400 group-hover:text-purple-300 transition-all duration-300 group-hover:gap-2">
                    Unlock <ArrowUpRight size={14} />
                  </span>
                )}

                {/* Thin progress underline — paid + started */}
                {isPaid && trackStarted && (
                  <div className="mt-1 h-1 bg-zinc-900/80 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.round((done / Math.max(1, total)) * 100))}%` }}
                    />
                  </div>
                )}

                {locked && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-purple-300/80 text-[9px]" title="Members only">
                    <Lock size={9} strokeWidth={2.5} />
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
